import React, { Fragment, type ReactNode, useEffect, useMemo, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalytics } from '../analytics/use-analytics';
import { useEditionAwareness } from '../services/edition-awareness-context';
import { isEligibleForConfluenceEndUserRequestExperiment } from '../services/is-eligible-for-confluence-end-user-request';
import { isEligibleForManualFeatureOnboarding } from '../services/is-eligible-for-manual-feature-onboarding';
import { isEligibleForServCoUplift } from '../services/is-eligible-for-service-collection-uplift';
import { isProductOneOf } from '../services/is-product-one-of';

import { ActionsAndContent } from './actions-and-content';

/**
 * One off event which tracks when a customer is eligible/ineligible for edition awareness
 */
function useTrackAudienceAndTiming(isEligible: boolean) {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'audienceAndTiming',
			actionSubjectId: 'editionAwareness',
			action: 'checked',
			attributes: {
				isEligible,
			},
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent, isEligible]);
}

/**
 * Tracks when a user should see the edition awareness experience
 */
function Analytics({ children }: { children: ReactNode }) {
	useTrackAudienceAndTiming(true);

	return <Fragment>{children}</Fragment>;
}

/**
 * Just sends ineligibility info through a `eligibilityCriteria checked` event.
 * This exists so we can optionally render this component
 */
export function IneligibleAnalytics() {
	useTrackAudienceAndTiming(false);

	return null;
}

/**
 * Based on the audience and timing (e.g. 3 days before the trial ends) we decide to show
 * the free/trial button to the customer. If we want to change the logic of if we show the button or
 * not, it should happen here.
 */
export function AudienceAndTiming(): React.JSX.Element | null {
	const context = useEditionAwareness();
	const {
		product,
		edition,
		isInTrial,
		isInPredunning,
		isInCollection,
		isEnterpriseCustomer,
		canUpgrade,
		invoiceableStatus,
		canAddPaymentDetails,
		canDirectBuyUpgrade,
	} = context;

	const isInTrialOrPredunning = isInTrial || isInPredunning;

	const shouldSeeManualFeatureOnboarding = isEligibleForManualFeatureOnboarding(context);

	const shouldSeeButton = useMemo(() => {
		if (isEnterpriseCustomer) {
			// EA should not be shown to Enterprise customers as per: https://atlassiansupport.atlassian.net/browse/CES-106245
			return false;
		}

		/* Avoid complex logic for collection
		// Collection starts with new stream of logic to see if we should show the button
		// We should avoid products-specific complex logic for collection
		//
		// Collection uses different logic for edition awareness
		// As required by Team EU. EA is shown for Teamwork Collection when user has PDE.
		// */
		if (isInCollection) {
			if (isProductOneOf(product, ['jira', 'confluence'])) {
				// Teamwork Collection EA is shown to all customers in collection
				// by identifying product is Jira, Confluence or Loon to identify Teamwork Collection
				// This is temporary solution to identify Teamwork Collection
				return true;
			}

			//intentional leave this no default return to avoid block other collection handler
		}

		if (product === 'jira-product-discovery' && fg('jpd_disable_edition_awareness')) {
			return false;
		}

		if (edition === 'free' && (canUpgrade || canDirectBuyUpgrade)) {
			return true;
		}

		if (isEligibleForConfluenceEndUserRequestExperiment(context)) {
			return true;
		}

		if (
			edition === 'standard' &&
			isInTrialOrPredunning &&
			invoiceableStatus === 'NOT_INVOICEABLE' &&
			canAddPaymentDetails
		) {
			return true;
		}
		if (
			edition === 'premium' &&
			isProductOneOf(product, ['jira', 'jira-service-management', 'jira-product-discovery']) &&
			isInTrialOrPredunning &&
			invoiceableStatus === 'NOT_INVOICEABLE' &&
			canAddPaymentDetails
		) {
			return true;
		}

		if (shouldSeeManualFeatureOnboarding) {
			// As part of the manual trial onboarding experiment we are showing the button to customers on manual trial with payment details
			return true;
		}

		if (product === 'confluence' && edition === 'premium' && isInTrialOrPredunning) {
			// Confluence will always show the button for premium while a customer is in a trial or in predunning
			// If the customer can add payment, that is checked as part of rendering the dialog (as the dialog
			// will still show to the customer if they can not add payment details)
			return true;
		}

		if (isEligibleForServCoUplift(context)) {
			return true;
		}

		return false;
	}, [
		isEnterpriseCustomer,
		isInCollection,
		product,
		edition,
		canUpgrade,
		canDirectBuyUpgrade,
		isInTrialOrPredunning,
		invoiceableStatus,
		canAddPaymentDetails,
		shouldSeeManualFeatureOnboarding,
		context,
	]);

	if (!shouldSeeButton) {
		if (invoiceableStatus === 'INVOICEABLE' && isInTrial) {
			// Like below, we're firing the analytics event, but also including a span to test the VC90 impact
			return (
				<>
					<span data-testid="ea-expanded-audience" />
					<IneligibleAnalytics />
				</>
			);
		}

		if (edition === 'free' || isInTrialOrPredunning) {
			// We're only firing the ineligible analytics when we could've shown the button, not all instances
			// this is to ensure we don't send the event on every page load
			return <IneligibleAnalytics />;
		}

		return null;
	}

	return (
		<Analytics>
			<ActionsAndContent />
		</Analytics>
	);
}
